import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Country, State, City } from 'country-state-city';
import apiClient from '../lib/apiClient';
import generateIdempotencyKey from '../utils/idempotency';
import { openRazorpayCheckout } from '../lib/razorpay';
import { loadOrderDraft, saveOrderDraft, clearOrderDraft } from '../utils/orderDraft';
import { buildWorkspaceBillingPrefill, normalizeWorkspaceUser } from '../utils/workspaceUser';
import { getPhoneRequirementMessage, normalizePhoneForCountry, validatePhoneForCountry } from '../utils/phoneValidation';

const CORPUSIO_CHECKOUT_COLOR = '#3457D5';
const RAZORPAY_METHOD_PREFS = {
    card: { card: true, netbanking: false, wallet: false, emi: false, upi: false, paylater: false },
    upi:  { card: false, netbanking: false, wallet: false, emi: false, upi: true, paylater: false },
};

const PLAN_KEYS = {
    Standard: 'starter_workspace',
    Pro: 'growth_workspace',
    Enterprise: 'enterprise_workspace'
};

const PLAN_RANK = {
    starter_workspace: 1,
    growth_workspace: 2,
    enterprise_workspace: 3,
};

const PLAN_DISPLAY_NAMES = ['Standard', 'Pro', 'Enterprise'];

const normalizePlanName = (value) => {
    const raw = String(value || '').trim();
    const normalized = raw
        .replace(/\s+License$/i, '')
        .replace(/[_-]+workspace$/i, '')
        .replace(/[_-]+plan$/i, '')
        .replace(/[_-]+license$/i, '')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();
    return PLAN_DISPLAY_NAMES.find((name) => name.toLowerCase() === normalized) || 'Standard';
};

const getPlanDirection = (currentPlanName, nextPlanName) => {
    const currentKey = PLAN_KEYS[normalizePlanName(currentPlanName)] || PLAN_KEYS.Standard;
    const nextKey = PLAN_KEYS[normalizePlanName(nextPlanName)] || PLAN_KEYS.Standard;
    if (currentKey === nextKey) return 'same';
    return (PLAN_RANK[nextKey] || 0) > (PLAN_RANK[currentKey] || 0) ? 'upgrade' : 'downgrade';
};

const formatPlanChangeDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date);
};

const ZIP_PLUS_FOUR = /^\d{5}(?:-\d{4})?$/;
const CANADA_POSTAL = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/i;
const UK_POSTAL = /^([A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}|GIR 0AA)$/i;
const AU_POSTAL = /^\d{4}$/;

const ADDRESS_FORMATS = {
    DEFAULT: {
        stateLabel: 'State / Region',
        statePlaceholder: 'Enter region / state',
        stateRequired: false,
        stateInputOnly: false,
        cityLabel: 'City',
        cityPlaceholder: 'Enter city',
        postalLabel: 'Postal / ZIP Code',
        postalPlaceholder: 'e.g., 560032',
        postalPattern: null,
        postalHint: '',
        requiresCounty: false,
        countyLabel: 'County / District',
        countyPlaceholder: 'Enter county / district',
    },
    US: {
        stateLabel: 'State',
        stateRequired: true,
        cityLabel: 'City',
        postalLabel: 'ZIP code',
        postalPlaceholder: 'e.g., 94105',
        postalPattern: ZIP_PLUS_FOUR,
        postalHint: 'Enter a valid 5-digit ZIP or ZIP+4 code.',
    },
    CA: {
        stateLabel: 'Province',
        stateRequired: true,
        cityLabel: 'City',
        postalLabel: 'Postal code',
        postalPlaceholder: 'e.g., H3A 2K6',
        postalPattern: CANADA_POSTAL,
        postalHint: 'Format: A1A 1A1',
    },
    GB: {
        stateLabel: 'County',
        stateInputOnly: true,
        stateRequired: true,
        cityLabel: 'Town / City',
        postalLabel: 'Postcode',
        postalPlaceholder: 'e.g., SW1A 1AA',
        postalPattern: UK_POSTAL,
        postalHint: 'Enter a valid UK postcode (SW1A 1AA).',
        requiresCounty: true,
        countyLabel: 'County',
        countyPlaceholder: 'e.g., Greater London',
    },
    AU: {
        stateLabel: 'State / Territory',
        stateRequired: true,
        cityLabel: 'City / Suburb',
        postalLabel: 'Postcode',
        postalPlaceholder: 'e.g., 2000',
        postalPattern: AU_POSTAL,
        postalHint: 'Four-digit Australian postcode.',
    },
    NZ: {
        stateLabel: 'Region',
        stateInputOnly: true,
        cityLabel: 'City / Town',
        postalLabel: 'Postcode',
        postalPlaceholder: 'e.g., 6011',
    },
    SG: {
        stateInputOnly: true,
        stateLabel: 'Region',
        cityLabel: 'City',
        postalLabel: 'Postal code',
        postalPlaceholder: 'e.g., 238801',
    },
    AE: {
        stateLabel: 'Emirate',
        stateRequired: true,
        cityLabel: 'City',
        postalLabel: 'PO Box',
        postalPlaceholder: 'e.g., 12345',
    },
};

const PaymentStatusOverlay = ({ stage, message, error, onRetry, onClose }) => {
    if (stage === 'idle') return null;

    const isSuccess = stage === 'success';
    const isError = stage === 'error';
    const isProcessing = stage === 'processing' || stage === 'verifying';

    return (
        <div style={{
            position: 'absolute',
            inset: 0,
            background: '#fff',
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px',
            textAlign: 'center',
            animation: 'fadeIn 0.3s ease-in-out',
        }}>
            {isProcessing && (
                <>
                    <div className="animate-spin" style={{
                        width: '48px',
                        height: '48px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #3457D5',
                        borderRadius: '50%',
                        marginBottom: '24px'
                    }}></div>
                    <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>{message}</h3>
                    <p style={{ color: '#6B7280' }}>Please do not close this window or refresh the page.</p>
                </>
            )}
            {isSuccess && (
                <>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        background: '#DCFCE7',
                        color: '#166534',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        marginBottom: '24px'
                    }}>✓</div>
                    <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>Payment Successful!</h3>
                    <p style={{ color: '#6B7280', marginBottom: '24px' }}>Your subscription has been activated successfully.</p>
                </>
            )}
            {isError && (
                <>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        background: '#FEE2E2',
                        color: '#B91C1C',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        marginBottom: '24px'
                    }}>!</div>
                    <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>Payment Failed</h3>
                    <p style={{ color: '#6B7280', marginBottom: '24px' }}>{error || 'Something went wrong during the payment process.'}</p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button type="button" onClick={onRetry} style={{ padding: '12px 24px', background: '#3457D5', color: '#fff', borderRadius: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' }}>Try Again</button>
                        <button type="button" onClick={onClose} style={{ padding: '12px 24px', background: '#F3F4F6', color: '#374151', borderRadius: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' }}>Cancel</button>
                    </div>
                </>
            )}
        </div>
    );
};

const OrderModal = ({
    onClose,
    onSuccess,
    initialValues = {},
    user = {},
    profileSeed = null,
    onRequirePhoneVerification,
    onRequireEmailVerification,
    requiresEmailVerification = false
}) => {
    const countries = useMemo(() => Country.getAllCountries(), []);
    const normalizedUser = useMemo(() => normalizeWorkspaceUser(user || {}), [user]);
    const resolvedProfileSeed = useMemo(() => {
        const baseProfileSeed = buildWorkspaceBillingPrefill(normalizedUser);
        const mergedProfileSeed = { ...baseProfileSeed };
        Object.entries(profileSeed || {}).forEach(([key, value]) => {
            if (typeof value === 'string' ? value.trim() : value) {
                mergedProfileSeed[key] = value;
            }
        });
        return mergedProfileSeed;
    }, [normalizedUser, profileSeed]);
    const phoneVerified = Boolean(normalizedUser?.phoneverified || normalizedUser?.phone_verified);
    const accountType = String(normalizedUser?.accounttype || normalizedUser?.account_type || '').toLowerCase()
        || (normalizedUser?.organizationname || normalizedUser?.organization_name || normalizedUser?.company ? 'company' : 'individual');
    const organizationRequired = accountType === 'company';
    const isPlanChange = initialValues?.mode === 'plan_change' && Boolean(initialValues?.planChangeForOrder);
    const currentPlanName = normalizePlanName(initialValues?.currentPlanName || '');
    const planChangeTargetOrder = String(initialValues?.planChangeForOrder || '').trim();
    const pendingChangeEffectiveAt = formatPlanChangeDate(initialValues?.pendingChangeEffectiveAt);
    const pendingPlanName = initialValues?.pendingPlanName
        ? normalizePlanName(initialValues.pendingPlanName)
        : '';

    const [loading, setLoading] = useState(false);
    const [paymentStage, setPaymentStage] = useState('idle'); // idle, processing, verifying, success, error
    const [error, setError] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [hydrated, setHydrated] = useState(false);
    const [draftTimestamp, setDraftTimestamp] = useState(null);
    const [hasDraft, setHasDraft] = useState(() => (isPlanChange ? false : !!loadOrderDraft()));

    const [selectedPlan, setSelectedPlan] = useState(normalizePlanName(initialValues.productType));
    const isSamePlanSelection = isPlanChange && normalizePlanName(selectedPlan) === currentPlanName;
    const planChangeDirection = isPlanChange ? getPlanDirection(currentPlanName, selectedPlan) : 'upgrade';
    const isScheduledDowngrade = isPlanChange && planChangeDirection === 'downgrade';
    const [quantity, setQuantity] = useState(initialValues.quantity || 1);
    const [organization, setOrganization] = useState('');
    const [details, setDetails] = useState('');

    const prevCountryRef = useRef('');
    const profileBackfillAttemptedRef = useRef(false);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [phoneCountryCode, setPhoneCountryCode] = useState('IN');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('all');

    const [county, setCounty] = useState('');
    const addressFormat = useMemo(() => {
        const normalizedCountry = (selectedCountry || '').toUpperCase();
        const overrides = ADDRESS_FORMATS[normalizedCountry] || {};
        return { ...ADDRESS_FORMATS.DEFAULT, ...overrides };
    }, [selectedCountry]);

    const plans = [
        { name: 'Standard', price: 1, priceLabel: '₹1', features: 'Essential AI tools for individuals' },
        { name: 'Pro', price: 1, priceLabel: '₹1', features: 'Advanced analytics & priority support' },
        { name: 'Enterprise', price: 699, priceLabel: '$699', features: 'Full customisation & dedicated manager' },
    ];

    const applyInitialValues = useCallback((values = {}) => {
        setSelectedPlan(normalizePlanName(values.productType));
        setQuantity(values.quantity || 1);
        setSelectedCountry(values.selectedCountry || '');
        setSelectedState(values.selectedState || '');
        setSelectedCity(values.selectedCity || '');
        setPhoneCountryCode(values.phoneCountryCode || 'IN');
        setPhoneNumber(values.phoneNumber || '');
        setOrganization(values.organization || '');
        setDetails(values.details || '');
        setPostalCode(values.postalCode || '');
        setCounty(values.county || '');
    }, []);

    const buildHydratedValues = useCallback((profileData = {}, incoming = {}, draft = null) => {
        const pickString = (...values) => {
            for (const value of values) {
                if (typeof value === 'string' && value.trim()) return value;
            }
            return '';
        };
        const pickNumber = (...values) => {
            for (const value of values) {
                const numeric = Number(value);
                if (Number.isFinite(numeric) && numeric > 0) return numeric;
            }
            return 1;
        };

        return {
            productType: pickString(
                draft?.selectedPlan,
                incoming?.productType,
                profileData.productType,
                'Standard'
            ) || 'Standard',
            quantity: pickNumber(draft?.quantity, incoming?.quantity, profileData.quantity, 1),
            selectedCountry: pickString(draft?.selectedCountry, incoming?.selectedCountry, profileData.selectedCountry),
            selectedState: pickString(draft?.selectedState, incoming?.selectedState, profileData.selectedState),
            selectedCity: pickString(draft?.selectedCity, incoming?.selectedCity, profileData.selectedCity),
            phoneCountryCode: pickString(draft?.phoneCountryCode, incoming?.phoneCountryCode, profileData.phoneCountryCode, 'IN') || 'IN',
            phoneNumber: pickString(draft?.phoneNumber, incoming?.phoneNumber, profileData.phoneNumber),
            organization: pickString(draft?.organization, incoming?.organization, profileData.organization),
            details: pickString(draft?.details, incoming?.details, profileData.details),
            postalCode: pickString(draft?.postalCode, incoming?.postalCode, profileData.postalCode),
            county: pickString(draft?.county, incoming?.county, profileData.county),
        };
    }, []);


    useEffect(() => {
        if (!selectedCountry) {
            setStates([]);
            if (!addressFormat.stateInputOnly) {
                setSelectedState('');
            }
            return;
        }
        const nextStates = State.getStatesOfCountry(selectedCountry) || [];
        setStates(nextStates);
        if (
            !addressFormat.stateInputOnly &&
            selectedState &&
            !nextStates.find((s) => s.isoCode === selectedState)
        ) {
            setSelectedState('');
        }
    }, [selectedCountry, selectedState, addressFormat.stateInputOnly]);

    useEffect(() => {
        if (!selectedCountry || !selectedState) {
            setCities([]);
            setSelectedCity('');
            return;
        }
        const nextCities = City.getCitiesOfState(selectedCountry, selectedState) || [];
        setCities(nextCities);
        if (selectedCity && !nextCities.find((c) => c.name === selectedCity)) {
            setSelectedCity('');
        }
    }, [selectedCountry, selectedState, selectedCity]);

    useEffect(() => {
        if (!selectedCountry) return;
        if (!phoneCountryCode || phoneCountryCode === prevCountryRef.current) {
            setPhoneCountryCode(selectedCountry);
        }
        prevCountryRef.current = selectedCountry;
    }, [selectedCountry, phoneCountryCode]);

    const resolveCountryCode = useCallback((value) => {
        if (!value) return '';
        const normalized = String(value).trim();
        const byIso = countries.find((c) => c.isoCode.toUpperCase() === normalized.toUpperCase());
        if (byIso) return byIso.isoCode;
        const byName = countries.find((c) => c.name.toLowerCase() === normalized.toLowerCase());
        return byName ? byName.isoCode : '';
    }, [countries]);

    const resolveStateCode = useCallback((countryCode, value) => {
        if (!countryCode || !value) return '';
        const pool = State.getStatesOfCountry(countryCode) || [];
        const normalized = String(value).trim();
        const byIso = pool.find((s) => s.isoCode.toUpperCase() === normalized.toUpperCase());
        if (byIso) return byIso.isoCode;
        const byName = pool.find((s) => s.name.toLowerCase() === normalized.toLowerCase());
        return byName ? byName.isoCode : '';
    }, []);

    const normalizeHydratedValues = useCallback((values = {}) => {
        const pickString = (...candidates) => {
            for (const candidate of candidates) {
                if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
            }
            return '';
        };

        const normalizedCountry = resolveCountryCode(
            pickString(
                values.selectedCountry,
                values.country,
                values.addresscountry,
                values.address_country,
                values.phoneCountryCode,
                values.phonecountry,
                values.phoneCountry,
                values.phone_country
            )
        );

        const normalizedState = normalizedCountry
            ? resolveStateCode(
                normalizedCountry,
                pickString(
                    values.selectedState,
                    values.state,
                    values.addressstate,
                    values.address_state
                )
            )
            : '';

        const normalizedPhoneCountry = (() => {
            const rawPhoneCountry = pickString(
                values.phoneCountryCode,
                values.phonecountry,
                values.phoneCountry,
                values.phone_country,
                normalizedCountry,
                'IN'
            );
            const resolved = resolveCountryCode(rawPhoneCountry);
            if (resolved) return resolved;
            return normalizedCountry || 'IN';
        })();

        return {
            productType: normalizePlanName(values.productType),
            quantity: values.quantity || 1,
            selectedCountry: normalizedCountry,
            selectedState: normalizedState || pickString(values.selectedState, values.state, values.addressstate, values.address_state),
            selectedCity: pickString(values.selectedCity, values.city, values.addresscity, values.address_city),
            phoneCountryCode: normalizedPhoneCountry,
            phoneNumber: normalizePhoneForCountry(
                pickString(values.phoneNumber, values.phone),
                normalizedPhoneCountry,
                countries
            ) || pickString(values.phoneNumber, values.phone),
            organization: pickString(
                values.organization,
                values.organizationname,
                values.organization_name,
                values.organizationName,
                values.company
            ),
            details: pickString(values.details),
            postalCode: pickString(
                values.postalCode,
                values.addresszipcode,
                values.address_zipcode,
                values.zipcode,
                values.zip_code,
                values.postalcode,
                values.zip
            ),
            county: pickString(values.county, values.addresscounty, values.address_county, values.district),
        };
    }, [countries, resolveCountryCode, resolveStateCode]);

    const buildProfileSeed = useCallback((profile = {}) => ({
        selectedCountry:
            profile.addresscountry ||
            profile.address_country ||
            profile.country ||
            profile.phonecountry ||
            profile.phoneCountry ||
            profile.phone_country ||
            profile.phoneCountryCode ||
            '',
        selectedState:
            profile.addressstate ||
            profile.address_state ||
            profile.state ||
            '',
        selectedCity:
            profile.addresscity ||
            profile.address_city ||
            profile.city ||
            '',
        phoneCountryCode:
            profile.phonecountry ||
            profile.phoneCountry ||
            profile.phone_country ||
            profile.phoneCountryCode ||
            '',
        phoneNumber: profile.phone || '',
        postalCode:
            profile.addresszipcode ||
            profile.address_zipcode ||
            profile.zipcode ||
            profile.zip_code ||
            profile.postalcode ||
            profile.zip ||
            '',
        county:
            profile.addresscounty ||
            profile.address_county ||
            profile.county ||
            profile.district ||
            '',
        organization:
            profile.organizationname ||
            profile.organization_name ||
            profile.organizationName ||
            profile.company ||
            '',
    }), []);

    const hasMeaningfulProfileSeed = useCallback((profile = {}) =>
        Boolean(
            profile.selectedCountry ||
            profile.selectedState ||
            profile.selectedCity ||
            profile.phoneCountryCode ||
            profile.phoneNumber ||
            profile.postalCode ||
            profile.county ||
            profile.organization
        ),
    []);

    useEffect(() => {
        const profileData = {
            ...buildProfileSeed(normalizedUser || {}),
            ...resolvedProfileSeed,
        };

        const shouldBackfillHydratedProfile =
            hydrated &&
            hasMeaningfulProfileSeed(profileData) &&
            !selectedCountry &&
            !selectedState &&
            !selectedCity &&
            !phoneNumber &&
            !postalCode &&
            !organization;

        if (hydrated && !shouldBackfillHydratedProfile) return;

        const metadataKeys = new Set([
            '__timestamp',
            'savedAt',
            'mode',
            'planChangeForOrder',
            'currentPlanName',
            'source',
        ]);

        const draft = isPlanChange ? null : loadOrderDraft();

        const meaningfulInitialKeys = Object.keys(initialValues || {}).filter((key) => {
            if (metadataKeys.has(key)) return false;
            const value = initialValues[key];
            if (typeof value === 'string') return value.trim().length > 0;
            if (typeof value === 'number') return Number.isFinite(value);
            return value !== undefined && value !== null;
        });
        const hasMeaningfulInitialValues = meaningfulInitialKeys.length > 0;

        if (hasMeaningfulInitialValues || draft) {
            const mergedData = buildHydratedValues(profileData, initialValues, draft);
            applyInitialValues(normalizeHydratedValues(mergedData));
            if (draft) {
                setDraftTimestamp(draft.savedAt || null);
                setHasDraft(true);
            } else {
                setDraftTimestamp(initialValues.savedAt || null);
                setHasDraft(Boolean(initialValues.savedAt));
            }
        } else {
            const finalData = buildHydratedValues(profileData, { productType: 'Standard', quantity: 1 }, null);
            applyInitialValues(normalizeHydratedValues(finalData));
            setDraftTimestamp(null);
            setHasDraft(false);
        }
        setHydrated(true);
    }, [
        applyInitialValues,
        buildHydratedValues,
        buildProfileSeed,
        normalizedUser,
        resolvedProfileSeed,
        hydrated,
        initialValues,
        isPlanChange,
        normalizeHydratedValues,
        hasMeaningfulProfileSeed,
        selectedCountry,
        selectedState,
        selectedCity,
        phoneNumber,
        postalCode,
        organization,
    ]);

    const hasMeaningfulHydratedFields = Boolean(
        selectedCountry ||
        selectedState ||
        selectedCity ||
        phoneNumber ||
        postalCode ||
        organization
    );

    useEffect(() => {
        if (!hydrated || hasMeaningfulHydratedFields || profileBackfillAttemptedRef.current) return;
        if (!normalizedUser?.email) return;
        profileBackfillAttemptedRef.current = true;

        let isActive = true;
        (async () => {
            try {
                const { data } = await apiClient.get('/api/user');
                if (!isActive) return;
                const refreshedUser = normalizeWorkspaceUser(data || {});
                const refreshedProfile = {
                    ...buildProfileSeed(refreshedUser),
                    ...buildWorkspaceBillingPrefill(refreshedUser),
                };
                const restored = buildHydratedValues(refreshedProfile, initialValues, isPlanChange ? null : loadOrderDraft());
                applyInitialValues(normalizeHydratedValues(restored));
            } catch (refreshError) {
                console.error('Failed to backfill workspace profile for plan modal:', refreshError);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [
        applyInitialValues,
        buildHydratedValues,
        buildProfileSeed,
        hasMeaningfulHydratedFields,
        hydrated,
        initialValues,
        isPlanChange,
        normalizeHydratedValues,
        normalizedUser?.email,
    ]);

    useEffect(() => {
        if (!hydrated || isPlanChange) return;
        const timestamp = new Date().toISOString();
        const draft = {
            selectedPlan,
            quantity,
            selectedCountry,
            selectedState,
            selectedCity,
            postalCode,
            organization,
            details,
            phoneCountryCode,
            phoneNumber,
            county,
            savedAt: timestamp,
        };
        saveOrderDraft(draft);
        setHasDraft(true);
        setDraftTimestamp(timestamp);
    }, [
        hydrated,
        selectedPlan,
        quantity,
        selectedCountry,
        selectedState,
        selectedCity,
        organization,
        details,
        postalCode,
        phoneCountryCode,
        phoneNumber,
        county,
        isPlanChange,
    ]);

    const calculateTotal = () => {
        const plan = plans.find(p => p.name === selectedPlan);
        return plan ? plan.price * quantity : 0;
    };

    const validateForm = () => {
        const problems = {};
        if (isPlanChange) {
            return problems;
        }
        if (!isPlanChange && organizationRequired && !organization.trim()) {
            problems.organization = 'Organization name is required for company accounts.';
        }
        if (!selectedCountry) problems.selectedCountry = 'Select a country.';
        const requiresStateValue = addressFormat.stateRequired || (!!states.length && !addressFormat.stateInputOnly);
        if (requiresStateValue) {
            if (addressFormat.stateInputOnly) {
                if (!selectedState.trim()) {
                    problems.selectedState = `${addressFormat.stateLabel} is required.`;
                }
            } else if (states.length && !selectedState) {
                problems.selectedState = `Select a ${addressFormat.stateLabel.toLowerCase()}.`;
            }
        }
        if (!selectedCity.trim()) problems.selectedCity = `${addressFormat.cityLabel} is required.`;
        if (addressFormat.requiresCounty && !county.trim()) {
            problems.county = `${addressFormat.countyLabel} is required.`;
        }
        const trimmedPostal = postalCode.trim();
        if (!trimmedPostal) {
            problems.postalCode = `${addressFormat.postalLabel} is required.`;
        } else if (addressFormat.postalPattern && !addressFormat.postalPattern.test(trimmedPostal)) {
            problems.postalCode = addressFormat.postalHint || 'Enter a valid postal code.';
        }
        if (!phoneCountryCode) {
            problems.phoneCountryCode = 'Phone country code required.';
        } else {
            const phoneValidation = validatePhoneForCountry(phoneNumber, phoneCountryCode, countries);
            if (!phoneValidation.isValid) {
                problems.phoneNumber = phoneValidation.message;
            }
        }
        if (!isPlanChange && !details.trim()) {
            problems.details = 'Please describe how you plan to deploy Corpusio.';
        }
        return problems;
    };

    const handleDeleteDraft = () => {
        clearOrderDraft();
        setHasDraft(false);
        setDraftTimestamp(null);
        setValidationErrors({});
        setStatusMessage('');
        const profileData = {
            ...buildProfileSeed(normalizedUser || {}),
            ...resolvedProfileSeed,
        };
        const restored = buildHydratedValues(profileData, initialValues, null);
        applyInitialValues(normalizeHydratedValues(restored));
    };

    const selectedCountryName = countries.find((c) => c.isoCode === selectedCountry)?.name || '';
    const selectedStateName = states.find((s) => s.isoCode === selectedState)?.name || selectedState.trim() || '';

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSamePlanSelection) {
            setValidationErrors({});
            setError('');
            setStatusMessage('You are already on this workspace plan. Choose a different plan to make a billing change.');
            return;
        }
        if (requiresEmailVerification) {
            setError('Please verify your email address before placing an order.');
            onRequireEmailVerification?.();
            return;
        }
        if (!phoneVerified) {
            setError('Please verify your phone number before placing an order.');
            onRequirePhoneVerification?.();
            return;
        }
        setLoading(true);
        setPaymentStage('processing');
        setError('');
        setStatusMessage('');
        setValidationErrors({});

        try {
            if (!normalizedUser?.email) {
                throw new Error('Please sign in again to place an order.');
            }
            const phone = (normalizedUser.phone || '').trim();
            if (!phone) {
                throw new Error('We need a phone number (with country code) on your profile before processing payments.');
            }

            const formErrors = validateForm();
            if (Object.keys(formErrors).length) {
                setValidationErrors(formErrors);
                throw new Error('Please review the highlighted fields.');
            }

            if (isScheduledDowngrade && planChangeTargetOrder) {
                setStatusMessage('Scheduling your downgrade for the next billing date…');
                setPaymentStage('processing');
                const { data: scheduleResponse } = await apiClient.post(
                    `/api/orders/${encodeURIComponent(planChangeTargetOrder)}/schedule-plan-change`,
                    { product: PLAN_KEYS[selectedPlan] || PLAN_KEYS.Standard }
                );
                const effectiveAt = formatPlanChangeDate(
                    scheduleResponse?.pending_change_effective_at
                );
                setPaymentStage('success');
                setStatusMessage(
                    effectiveAt
                        ? `Downgrade scheduled. Your workspace will move to ${selectedPlan} on ${effectiveAt}. Renewal payment timing follows backend billing.`
                        : `Downgrade scheduled. Your workspace will move to ${selectedPlan} on the next billing date. Renewal payment timing follows backend billing.`
                );
                onSuccess?.();
                setTimeout(onClose, 1400);
                return;
            }

            const planKey = PLAN_KEYS[selectedPlan] || PLAN_KEYS.Standard;
            const customerKey = generateIdempotencyKey('customer');
            const orderKey = generateIdempotencyKey('order');

            const normalizedCity = selectedCity.trim();
            const normalizedCounty = county.trim();
            const normalizedPostal = postalCode.trim();

            const customerPayload = {
                name: normalizedUser.name || organization || 'Corpusio Customer',
                email: normalizedUser.email,
                contact: phone,
                notes: {
                    organization: organization || undefined,
                    country: selectedCountryName,
                    state: selectedStateName,
                    city: normalizedCity,
                    county: normalizedCounty || undefined,
                    phoneCountry: phoneCountryCode,
                }
            };

            setStatusMessage('Creating Razorpay customer…');
            setPaymentStage('processing');
            const { data: customerResponse } = await apiClient.post('/api/create_customer', customerPayload, {
                headers: { 'Idempotency-Key': customerKey }
            });
            const customer = customerResponse?.customer || customerResponse;
            const customerId = customer?.id;
            if (!customerId) {
                throw new Error('Unable to create Razorpay customer. Please retry.');
            }

            const shippingAddress = {
                country: selectedCountryName,
                state: selectedStateName,
                city: normalizedCity,
            };
            if (normalizedPostal) {
                shippingAddress.zipcode = normalizedPostal;
            }
            if (normalizedCounty) {
                shippingAddress.county = normalizedCounty;
            }

            // SUBSCRIPTION FLOW (using Order-based approach for Import Flow)
        const subscriptionPayload = {
            product: planKey,
            quantity,
            frequency: 'as_presented',
            ...(paymentMethod !== 'all' ? { method: paymentMethod } : {}),
        };
        subscriptionPayload.notes = {
            ...(subscriptionPayload.notes || {}),
            organization: organization || undefined,
            billing_country: selectedCountryName || undefined,
            billing_state: selectedStateName || undefined,
            billing_city: normalizedCity || undefined,
            billing_county: normalizedCounty || undefined,
            billing_postal: normalizedPostal || undefined,
        };
        if (isPlanChange && planChangeTargetOrder) {
            subscriptionPayload.notes = {
                ...subscriptionPayload.notes,
                plan_change_for_order: planChangeTargetOrder,
                plan_change_mode: 'true',
                previous_plan: currentPlanName || undefined,
                target_plan: selectedPlan,
            };
        }

        setStatusMessage('Creating subscription order…');
            setPaymentStage('processing');
            const { data: subResponse } = await apiClient.post('/api/create_subscription', subscriptionPayload, {
                headers: { 'Idempotency-Key': orderKey }
            });

            if (paymentMethod === 'upi' && Number(subResponse.amount || 0) > 10_000_000) {
                setPaymentMethod('card');
                throw new Error('UPI Autopay is unavailable for orders above ₹1,00,000. Please use Card instead.');
            }

            const config = window.__CB_CONFIG__ || window.__PUBLIC_CONFIG__ || {};
            const keyId =
                subResponse.key_id ||
                config.razorpayKeyId ||
                config.RAZORPAY_KEY_ID ||
                config.razorpay_key_id;

            if (!keyId) {
                throw new Error('Razorpay configuration missing. Please refresh.');
            }

            // Order is now in DB — draft is no longer needed regardless of whether
            // the user completes or cancels the Razorpay checkout.
            clearOrderDraft();
            setHasDraft(false);
            setDraftTimestamp(null);

            setStatusMessage('Opening Payment Checkout…');

            // Open Checkout with Order ID (Import Flow compatible)
            const checkoutCustomerId = subResponse.customer_id || customerId;
            
            if (!checkoutCustomerId || checkoutCustomerId.includes('fallback')) {
                console.error('❌ CRITICAL: Invalid or missing customer_id detected.');
            }

            const checkoutOptions = {
                key: keyId,
                order_id: subResponse.order_id,               // Order-based flow
                customer_id: checkoutCustomerId,              // Required for Import Flow
                recurring: subResponse.recurring || 'preferred',
                checkoutConfigId:
                    subResponse.checkout_config_id ||
                    config.razorpayCheckoutConfigId ||
                    config.RAZORPAY_CHECKOUT_CONFIG_ID ||
                    undefined,
                method: paymentMethod !== 'all' ? RAZORPAY_METHOD_PREFS[paymentMethod] : undefined,
                amount: subResponse.amount,
                currency: subResponse.currency || 'INR',
                name: 'Corpusio',
                description: subResponse.description || `${selectedPlan} - Monthly`,
                image: '/corpusio-logo.webp',

                // Pre-fill user details
                prefill: {
                    name: customerPayload.name,
                    email: normalizedUser.email,
                    contact: phone,
                    customer_id: checkoutCustomerId          // Explicitly pass in prefill too
                },
                notes: {
                    plan: planKey,
                    source: 'web_dashboard',
                    organization: organization || undefined,
                    invoice_number: `SUB-${Date.now()}`,
                    goods_description: `Corpusio ${selectedPlan} subscription`,
                    customer_id: checkoutCustomerId          // Also in notes for tracking
                },
                theme: { color: CORPUSIO_CHECKOUT_COLOR },
                modal: {
                    ondismiss: () => {}
                }
            };

            const payment = await openRazorpayCheckout(checkoutOptions);
            setPaymentStage('verifying');


            setStatusMessage('Finalizing payment…');
            const verifyKey = generateIdempotencyKey('verify-payment');
            const verification = new FormData();
            verification.append('razorpay_payment_id', payment.razorpay_payment_id);
            verification.append('razorpay_order_id', payment.razorpay_order_id);
            verification.append('razorpay_signature', payment.razorpay_signature);
            verification.append('product', planKey);
            verification.append('workspaceName', organization || normalizedUser.name || 'Corpusio Workspace');
            verification.append('dataConsent', '1');
            verification.append('consentVersion', 'corpusio-workspace-terms-v1');
            verification.append('consentText', 'Corpusio workspace data & usage terms accepted');
            verification.append('consentSignedAt', new Date().toISOString());
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) verification.append('consentTimezone', timezone);
            verification.append('yourPhone', phone);
            if (phoneCountryCode) verification.append('yourPhoneCountry', phoneCountryCode);
            if (selectedCountryName) verification.append('yourCountry', selectedCountryName);
            if (selectedStateName) verification.append('yourState', selectedStateName);
            if (normalizedCity) verification.append('yourCity', normalizedCity);
            if (normalizedCounty) verification.append('yourCounty', normalizedCounty);
            if (normalizedPostal) verification.append('yourPostal', normalizedPostal);
            if (details) verification.append('specialNote', details);
            if (isPlanChange && planChangeTargetOrder) {
                verification.append('plan_change_for_order', planChangeTargetOrder);
            }

            await apiClient.post('/api/verify_payment', verification, {
                headers: { 'Idempotency-Key': verifyKey }
            });

            setPaymentStage('success');
            setStatusMessage('Payment successful! Redirecting…');
            clearOrderDraft();
            onSuccess?.();
            setTimeout(onClose, 1200);
        } catch (err) {
            console.error('Order flow failed:', err);

            // User dismissed Razorpay (cancel / back button) — not a payment failure.
            // Close the modal and let the dashboard show the "Retry Payment" button.
            if (err?.message === 'Payment cancelled') {
                clearOrderDraft();
                onSuccess?.();   // triggers Dashboard to refresh orders + draft state
                onClose();
                return;
            }

            const requiresPhone = Boolean(
                err?.response?.data?.requiresphoneverification
                || err?.response?.data?.requires_phone_verification
                || err?.response?.data?.requiresPhoneVerification
                || err?.requiresPhoneVerification
            );
            const requiresEmail = Boolean(
                err?.response?.data?.requiresemailverification
                || err?.response?.data?.requires_email_verification
                || err?.requiresEmailVerification
            );
            const details = err?.response?.data?.details;
            const detailsText = typeof details === 'string'
                ? details
                : details?.error?.description || details?.error?.reason || '';
            const activeSubscriptionExists = err?.response?.data?.code === 'active_subscription_exists';
            const message = activeSubscriptionExists
                ? (err?.response?.data?.error || 'This workspace already has an active subscription. Manage it from Usage & Billing before starting another one.')
                : (err?.response?.data?.error || detailsText || err.message || 'Failed to complete payment. Please try again.');
            setError(message);
            setPaymentStage('error');
            if (requiresPhone) {
                onRequirePhoneVerification?.();
            }
            if (requiresEmail) {
                onRequireEmailVerification?.();
            }
            setStatusMessage('');
        } finally {
            setLoading(false);
            if (paymentStage !== 'success' && paymentStage !== 'error') {
                setPaymentStage('idle');
            }
        }
    };

    const renderError = (field) => {
        if (!validationErrors[field]) return null;
        return (
            <div style={{ color: '#B91C1C', fontSize: '12px', marginTop: '6px' }}>
                {validationErrors[field]}
            </div>
        );
    };

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px',
        },
        modal: {
            background: '#FFFFFF',
            width: '100%',
            maxWidth: '900px',
            borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
            overflow: 'hidden',
        },
        header: {
            padding: '24px 32px',
            borderBottom: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        title: {
            fontSize: '24px',
            fontWeight: '700',
            color: '#111827',
            margin: 0,
        },
        content: {
            padding: '32px',
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'minmax(0, 1.5fr) minmax(0, 1fr)',
            gap: '40px',
        },
        sectionTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '16px',
            display: 'block',
        },
        planGrid: {
            display: 'grid',
            gridTemplateColumns: window.innerWidth < 640 ? '1fr' : 'repeat(3, 1fr)',
            gap: '12px',
            marginBottom: '24px',
        },
        planCard: (isActive) => ({
            padding: '16px',
            border: isActive ? '2px solid #3457D5' : '1px solid #E5E7EB',
            borderRadius: '12px',
            cursor: 'pointer',
            background: isActive ? '#EEF2FF' : '#FFFFFF',
            transition: 'all 0.2s',
        }),
        planName: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '4px',
        },
        planPrice: {
            fontSize: '20px',
            fontWeight: '700',
            color: '#3457D5',
            marginBottom: '8px',
        },
        planFeat: {
            fontSize: '12px',
            color: '#6B7280',
            lineHeight: '1.4',
        },
        formGroup: {
            marginBottom: '20px',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px',
        },
        requiredMark: {
            color: '#B91C1C',
            fontWeight: '700',
            marginLeft: '4px',
        },
        input: {
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #D1D5DB',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s',
        },
        select: {
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #D1D5DB',
            fontSize: '14px',
            outline: 'none',
            background: '#fff',
            cursor: 'pointer',
        },
        statusBox: {
            background: '#EEF2FF',
            color: '#1D4ED8',
            padding: '12px',
            borderRadius: '10px',
            marginBottom: '20px',
            fontSize: '14px',
        },
        errorBox: {
            background: '#FEE2E2',
            color: '#B91C1C',
            padding: '12px',
            borderRadius: '10px',
            marginBottom: '20px',
            fontSize: '14px',
        },
        draftMeta: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            background: '#F3F4F6',
            borderRadius: '10px',
            padding: '10px 14px',
            fontSize: '12px',
            color: '#374151',
            marginBottom: '16px',
        },
        draftMetaButton: {
            border: 'none',
            background: '#FFE4E6',
            color: '#B91C1C',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
        },
        summaryCard: {
            background: '#F9FAFB',
            padding: '24px',
            borderRadius: '16px',
            height: 'fit-content',
        },
        row: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '12px',
            fontSize: '14px',
            color: '#4B5563',
        },
        totalRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #E5E7EB',
            fontSize: '18px',
            fontWeight: '700',
            color: '#111827',
        },
        helperText: {
            marginTop: '6px',
            fontSize: '12px',
            color: '#6B7280',
        },
        primaryBtn: {
            width: '100%',
            padding: '16px',
            background: '#3457D5',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background 0.2s',
        },
        cancelBtn: {
            background: 'none',
            border: 'none',
            color: '#6B7280',
            cursor: 'pointer',
            fontSize: '24px',
        }
    };

    const showStateSelect = !addressFormat.stateInputOnly && states.length > 0;
    const showStateInput = addressFormat.stateInputOnly || !states.length;
    const hasCityDropdown = showStateSelect && selectedState && cities.length > 0;
    const postalPatternAttr = addressFormat.postalPattern ? addressFormat.postalPattern.source : undefined;

    return (
        <div style={styles.overlay}>
            <div style={{ ...styles.modal, position: 'relative' }}>
                <PaymentStatusOverlay 
                    stage={paymentStage} 
                    message={statusMessage} 
                    error={error} 
                    onRetry={() => setPaymentStage('idle')} 
                    onClose={onClose} 
                />
                <div style={styles.header}>
                    <h2 style={styles.title}>{isPlanChange ? 'Update Workspace Plan' : 'Choose Your Workspace Plan'}</h2>
                    <button onClick={onClose} style={styles.cancelBtn} aria-label="Close order modal">&times;</button>
                </div>

                <div style={styles.content}>
                    <div>
                        {error && <div style={styles.errorBox}>{error}</div>}
                        {requiresEmailVerification && (
                            <div style={styles.statusBox}>
                                Email verification is required before we can activate a subscription. Use the dashboard banner to resend the verification link.
                            </div>
                        )}
                        {statusMessage && !error && (
                            <div style={styles.statusBox}>{statusMessage}</div>
                        )}
                        {isPlanChange && currentPlanName && (
                            <div style={styles.statusBox}>
                                {isSamePlanSelection ? (
                                    <>
                                        You are already on the <strong>{currentPlanName}</strong> plan, so no billing change is needed unless you choose a different plan.
                                    </>
                                ) : isScheduledDowngrade ? (
                                    <>
                                        You are scheduling a downgrade from <strong>{currentPlanName}</strong> to <strong>{selectedPlan}</strong>. The lower plan starts on the next billing date, and any renewal payment due before then comes from backend billing.
                                    </>
                                ) : (
                                    <>
                                        You are upgrading from <strong>{currentPlanName}</strong> to <strong>{selectedPlan}</strong>. The charge happens immediately, the billing date resets to today, and the existing subscription is updated instead of creating a second active plan.
                                    </>
                                )}
                            </div>
                        )}
                        {isPlanChange && pendingPlanName && pendingChangeEffectiveAt && (
                            <div style={styles.statusBox}>
                                A downgrade to <strong>{pendingPlanName}</strong> is already scheduled for <strong>{pendingChangeEffectiveAt}</strong>.
                            </div>
                        )}
                        {draftTimestamp && !isPlanChange && (
                            <div style={styles.draftMeta}>
                                <span>Draft auto-saved at {new Date(draftTimestamp).toLocaleString()}</span>
                                {hasDraft && (
                                    <button type="button" style={styles.draftMetaButton} onClick={handleDeleteDraft}>
                                        Delete Draft
                                    </button>
                                )}
                            </div>
                        )}

                        <div style={styles.formGroup}>
                            <label style={styles.sectionTitle}>{isPlanChange ? 'Select New Plan' : 'Select Plan'}</label>
                            <div style={styles.planGrid}>
                                {plans.map(plan => (
                                    <div
                                        key={plan.name}
                                        style={styles.planCard(selectedPlan === plan.name)}
                                        onClick={() => {
                                            setSelectedPlan(plan.name);
                                            setValidationErrors((prev) => ({ ...prev, selectedPlan: undefined }));
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div style={styles.planName}>{plan.name}</div>
                                        <div style={styles.planPrice}>{plan.priceLabel}</div>
                                        <div style={styles.planFeat}>{plan.features}</div>
                                    </div>
                                ))}
                            </div>
                            {renderError('selectedPlan')}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Country</label>
                                <select
                                    style={styles.select}
                                    value={selectedCountry}
                                    onChange={(e) => {
                                        setSelectedCountry(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, selectedCountry: undefined }));
                                    }}
                                >
                                    <option value="">Select Country...</option>
                                    {countries.map(c => (
                                        <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                                    ))}
                                </select>
                                {renderError('selectedCountry')}
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>{addressFormat.stateLabel}</label>
                                {showStateSelect ? (
                                    <select
                                        style={styles.select}
                                        value={selectedState}
                                        onChange={(e) => {
                                            setSelectedState(e.target.value);
                                            setValidationErrors((prev) => ({ ...prev, selectedState: undefined }));
                                        }}
                                        disabled={!selectedCountry}
                                    >
                                        <option value="">{`Select ${addressFormat.stateLabel}...`}</option>
                                        {states.map(s => (
                                            <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                                        ))}
                                    </select>
                                ) : showStateInput ? (
                                    <input
                                        style={styles.input}
                                        value={selectedState}
                                        onChange={(e) => {
                                            setSelectedState(e.target.value);
                                            setValidationErrors((prev) => ({ ...prev, selectedState: undefined }));
                                        }}
                                        placeholder={addressFormat.statePlaceholder}
                                        disabled={!selectedCountry}
                                    />
                                ) : null}
                                {renderError('selectedState')}
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>{addressFormat.cityLabel}</label>
                            {hasCityDropdown ? (
                                <select
                                    style={styles.select}
                                    value={selectedCity}
                                    onChange={(e) => {
                                        setSelectedCity(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, selectedCity: undefined }));
                                    }}
                                    disabled={!selectedState}
                                >
                                    <option value="">{`Select ${addressFormat.cityLabel}...`}</option>
                                    {cities.map((city) => (
                                        <option key={city.name} value={city.name}>{city.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    style={styles.input}
                                    value={selectedCity}
                                    onChange={(e) => {
                                        setSelectedCity(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, selectedCity: undefined }));
                                    }}
                                    placeholder={addressFormat.cityPlaceholder}
                                    disabled={!selectedCountry}
                                />
                            )}
                            {renderError('selectedCity')}
                        </div>

                        {addressFormat.requiresCounty && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>{addressFormat.countyLabel}</label>
                                <input
                                    style={styles.input}
                                    value={county}
                                    onChange={(e) => {
                                        setCounty(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, county: undefined }));
                                    }}
                                    placeholder={addressFormat.countyPlaceholder}
                                    disabled={!selectedCountry}
                                />
                                {renderError('county')}
                            </div>
                        )}

                        <div style={styles.formGroup}>
                            <label style={styles.label}>{addressFormat.postalLabel}</label>
                            <input
                                style={styles.input}
                                value={postalCode}
                                onChange={(e) => {
                                    setPostalCode(e.target.value);
                                    setValidationErrors((prev) => ({ ...prev, postalCode: undefined }));
                                }}
                                placeholder={addressFormat.postalPlaceholder}
                                disabled={!selectedCountry}
                                pattern={postalPatternAttr}
                                title={addressFormat.postalHint || undefined}
                            />
                            {renderError('postalCode')}
                            {addressFormat.postalHint && (
                                <div style={styles.helperText}>{addressFormat.postalHint}</div>
                            )}
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Phone Country Code</label>
                            <select
                                style={styles.select}
                                value={phoneCountryCode}
                                onChange={(e) => {
                                    setPhoneCountryCode(e.target.value);
                                    setValidationErrors((prev) => ({ ...prev, phoneCountryCode: undefined }));
                                }}
                            >
                                <option value="">Select code…</option>
                                {countries.map((c) => (
                                    <option key={`dial-${c.isoCode}`} value={c.isoCode}>
                                        +{c.phonecode || '--'} ({c.name})
                                    </option>
                                ))}
                            </select>
                            {renderError('phoneCountryCode')}
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Phone Number</label>
                            <input
                                style={styles.input}
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => {
                                    setPhoneNumber(e.target.value);
                                    setValidationErrors((prev) => ({ ...prev, phoneNumber: undefined }));
                                }}
                                inputMode="numeric"
                                placeholder="e.g. 9876543210"
                            />
                            {renderError('phoneNumber')}
                            {!validationErrors.phoneNumber && (
                                <div style={styles.helperText}>
                                    {getPhoneRequirementMessage(phoneCountryCode || selectedCountry || 'IN')}
                                </div>
                            )}
                        </div>

                        {!isPlanChange && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    {organizationRequired ? 'Organization Name' : 'Workspace Name'}
                                    {organizationRequired && <span style={styles.requiredMark}>*</span>}
                                </label>
                                <input
                                    style={styles.input}
                                    value={organization}
                                    onChange={(e) => {
                                        setOrganization(e.target.value);
                                        setValidationErrors((prev) => ({ ...prev, organization: undefined }));
                                    }}
                                    placeholder={organizationRequired ? 'e.g. Acme Corp' : 'Optional name for your workspace'}
                                />
                                {renderError('organization')}
                            </div>
                        )}

                        {!isPlanChange && (
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    Deployment &amp; Purpose Details
                                    <span style={styles.requiredMark}>*</span>
                                </label>
                                <textarea
                                    style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                                    value={details}
                                    onChange={(e) => setDetails(e.target.value)}
                                    placeholder="Describe how your team will deploy Corpusio and the business goals."
                                    required
                                />
                                {renderError('details')}
                            </div>
                        )}
                    </div>

                    <div>
                        <div style={styles.summaryCard}>
                            <h3 style={{ ...styles.sectionTitle, marginBottom: '20px' }}>{isPlanChange ? 'Plan Change Summary' : 'Order Summary'}</h3>

                            <div style={styles.row}>
                                <span>Product</span>
                                <span style={{ fontWeight: 600 }}>Corpusio AI</span>
                            </div>
                            {isPlanChange && currentPlanName && (
                                <div style={styles.row}>
                                    <span>Current plan</span>
                                    <span style={{ fontWeight: 600 }}>{currentPlanName}</span>
                                </div>
                            )}
                            <div style={styles.row}>
                                <span>{isPlanChange ? 'New plan' : 'Plan'}</span>
                                <span style={{ fontWeight: 600 }}>{selectedPlan} License</span>
                            </div>

                            {!isPlanChange && (
                                <div style={{ ...styles.row, alignItems: 'center', marginTop: '20px' }}>
                                    <span>Quantity</span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={quantity}
                                        onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                        style={{ ...styles.input, width: '80px', padding: '8px' }}
                                    />
                                </div>
                            )}

                            <div style={styles.totalRow}>
                                <span>{isSamePlanSelection ? 'Billing change' : isScheduledDowngrade ? 'Backend billing' : 'Total'}</span>
                                <span style={{ color: '#3457D5' }}>
                                    {isSamePlanSelection
                                        ? 'No plan change'
                                        : isScheduledDowngrade
                                            ? 'Uses renewal schedule'
                                            : `$${calculateTotal().toLocaleString()}`}
                                </span>
                            </div>

                            {!isSamePlanSelection && !isScheduledDowngrade && (
                                <div style={{ marginTop: '20px', marginBottom: '4px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
                                        Payment Method
                                    </div>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        {[
                                            { value: 'all', label: 'All enabled', icon: '' },
                                            { value: 'card', label: 'Card', icon: '💳' },
                                            { value: 'upi', label: 'UPI', icon: '📱' },
                                        ].map(({ value, label, icon }) => (
                                            <label
                                                key={value}
                                                style={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '10px 14px',
                                                    borderRadius: '10px',
                                                    border: `2px solid ${paymentMethod === value ? '#3457D5' : '#E5E7EB'}`,
                                                    background: paymentMethod === value ? '#EFF6FF' : '#FAFAFA',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    color: paymentMethod === value ? '#1D4ED8' : '#374151',
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="paymentMethod"
                                                    value={value}
                                                    checked={paymentMethod === value}
                                                    onChange={() => setPaymentMethod(value)}
                                                    style={{ accentColor: '#3457D5' }}
                                                />
                                                {icon ? `${icon} ` : ''}{label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {isScheduledDowngrade && (
                                <div style={{ marginTop: '20px', padding: '12px 14px', borderRadius: '12px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '13px', lineHeight: 1.6, color: '#1E3A8A' }}>
                                    Your current plan remains active until the next billing date. The renewal amount and payment status are read from backend billing, then the scheduled downgrade takes effect.
                                </div>
                            )}

                            {isSamePlanSelection ? (
                                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        style={{ ...styles.primaryBtn, marginTop: 0 }}
                                    >
                                        Continue
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        style={{
                                            ...styles.primaryBtn,
                                            marginTop: 0,
                                            background: '#F3F4F6',
                                            color: '#374151',
                                        }}
                                    >
                                        Exit
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    style={{ ...styles.primaryBtn, marginTop: '24px', opacity: loading ? 0.7 : 1 }}
                                >
                                    {loading ? 'Processing...' : isPlanChange ? (isScheduledDowngrade ? 'Schedule Downgrade' : 'Upgrade Now') : 'Secure Checkout'}
                                </button>
                            )}

                            <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: '#6B7280', lineHeight: 1.5 }}>
                                <div>🔒 Encrypted • 🛡️ 30-Day Guarantee</div>
                                <div style={{ marginTop: '4px' }}>
                                    {isSamePlanSelection
                                        ? 'You are already on this plan. No payment or subscription update is required unless you choose a different plan.'
                                        : isScheduledDowngrade
                                        ? 'Downgrades are scheduled for the next billing date. Your current access remains unchanged until then.'
                                        : isPlanChange
                                        ? 'Upgrades are charged immediately, the billing date resets to today, and the current recurring subscription is updated without opening a second one.'
                                        : 'Corpusio workspaces renew monthly until cancelled. Accounts more than 30 days past-due are paused automatically.'}
                                </div>
                                {(selectedCity || selectedStateName || selectedCountryName || county || postalCode) && (
                                    <div style={{ marginTop: '6px', lineHeight: 1.4 }}>
                                        <div style={{ fontWeight: 600 }}>Billing Details</div>
                                        <div>
                                            {selectedCity && `${selectedCity}, `}
                                            {county && `${county}, `}
                                            {selectedStateName && `${selectedStateName}, `}
                                            {selectedCountryName}
                                        </div>
                                        {postalCode && (
                                            <div>{addressFormat.postalLabel}: {postalCode}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderModal;
